import { useState, useEffect } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { X, CalendarClock, Send, ImagePlus, Trash2, AlertTriangle } from "lucide-react";

export default function ScheduleDialog({ content, source, postId, initialImage, initialImageMeta, onClose, onScheduled }) {
  const now = new Date(Date.now() + 60 * 60 * 1000);
  const [date, setDate] = useState(now.toISOString().slice(0, 10));
  const [time, setTime] = useState(now.toTimeString().slice(0, 5));
  const [busy, setBusy] = useState(false);
  const [imageData, setImageData] = useState(initialImage || null);
  const [li, setLi] = useState(null);

  useEffect(() => {
    api.get("/linkedin/status").then((r) => setLi(r.data)).catch(() => {});
  }, []);

  const scheduledAfterExpiry = (() => {
    if (!li?.connected || li?.auto_renew || !li?.access_expires_at) return false;
    const sched = new Date(`${date}T${time}:00`).getTime();
    return sched > li.access_expires_at * 1000;
  })();

  const pickImage = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("Image trop lourde (max 5 Mo)."); return; }
    const reader = new FileReader();
    reader.onload = () => setImageData(reader.result);
    reader.readAsDataURL(file);
  };

  const buildPayload = (extra) => ({
    ...extra,
    ...(imageData && imageData.startsWith("data:image") ? { image_data: imageData } : {}),
    ...(initialImageMeta?.image_prompt ? { image_prompt: initialImageMeta.image_prompt } : {}),
    ...(initialImageMeta?.image_mode ? { image_mode: initialImageMeta.image_mode } : {}),
  });

  const schedule = async () => {
    setBusy(true);
    const scheduled_at = new Date(`${date}T${time}:00`).toISOString();
    try {
      if (postId) {
        await api.put(`/posts/${postId}`, buildPayload({ status: "scheduled", scheduled_at }));
      } else {
        await api.post("/posts", buildPayload({ content, status: "scheduled", scheduled_at, source }));
      }
      onScheduled();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const publishNow = async () => {
    setBusy(true);
    try {
      let id = postId;
      if (!id) {
        const { data } = await api.post("/posts", buildPayload({ content, status: "draft", source }));
        id = data.id;
      } else if (imageData) {
        await api.put(`/posts/${id}`, { image_data: imageData });
      }
      await api.post(`/posts/${id}/publish`);
      toast.success("Post publié.");
      onScheduled();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink-dark/50 backdrop-blur-sm p-4" onClick={onClose}>
      <div data-testid="schedule-dialog" className="bg-white border border-slate-200 rounded-sm w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <div className="flex items-center gap-2 text-coral-dark">
            <CalendarClock className="h-5 w-5" />
            <span className="font-serif text-xl">Planifier la publication</span>
          </div>
          <button data-testid="close-schedule-dialog" onClick={onClose} className="text-slate-400 hover:text-slate-800 transition-colors">
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="p-6 space-y-4">
          <p className="text-sm text-slate-500 leading-relaxed border border-slate-100 bg-slate-50 rounded-sm p-3 font-serif max-h-28 overflow-y-auto">
            {content.slice(0, 220)}{content.length > 220 ? "…" : ""}
          </p>

          <div>
            <label className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500">Image (optionnelle)</label>
            {imageData ? (
              <div className="mt-2 relative">
                <img src={imageData} alt="aperçu" className="w-full h-40 object-cover rounded-sm border border-slate-200" />
                <button data-testid="remove-image-btn" onClick={() => setImageData(null)}
                  className="absolute top-2 right-2 bg-white/90 border border-slate-200 rounded-sm p-1.5 text-slate-500 hover:text-destructive transition-colors">
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ) : (
              <label data-testid="image-upload-label" className="mt-2 flex items-center justify-center gap-2 border border-dashed border-slate-300 rounded-sm py-4 text-sm text-slate-500 cursor-pointer hover:border-coral hover:text-coral-dark transition-colors">
                <ImagePlus className="h-4 w-4" /> Ajouter une image
                <input data-testid="image-upload-input" type="file" accept="image/*" onChange={pickImage} className="hidden" />
              </label>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500">Date</label>
              <input data-testid="schedule-date" type="date" value={date} onChange={(e) => setDate(e.target.value)}
                className="mt-1.5 w-full border border-slate-200 rounded-sm px-3 py-2.5 text-sm outline-none focus:ring-1 focus:ring-coral" />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500">Heure</label>
              <input data-testid="schedule-time" type="time" value={time} onChange={(e) => setTime(e.target.value)}
                className="mt-1.5 w-full border border-slate-200 rounded-sm px-3 py-2.5 text-sm outline-none focus:ring-1 focus:ring-coral" />
            </div>
          </div>

          {scheduledAfterExpiry && (
            <div data-testid="schedule-expiry-warning" className="flex items-start gap-2.5 bg-amber-50 border border-amber-300 rounded-sm p-3">
              <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
              <p className="text-xs text-amber-800 leading-relaxed">
                Ce post est programmé <strong>après l'expiration de votre connexion LinkedIn</strong>
                {li?.days_until_expiry != null ? ` (dans ${li.days_until_expiry} jour(s))` : ""}. Il risque de ne pas être publié.
                Reconnectez LinkedIn depuis le tableau de bord avant cette date.
              </p>
            </div>
          )}
        </div>
        <div className="flex items-center gap-3 px-6 py-4 border-t border-slate-100">
          <button data-testid="confirm-schedule-btn" onClick={schedule} disabled={busy}
            className="flex-1 bg-coral-strong text-white py-2.5 rounded-sm text-sm font-semibold hover:bg-coral-hover transition-colors disabled:opacity-60">
            {busy ? "…" : "Programmer"}
          </button>
          <button data-testid="publish-now-btn" onClick={publishNow} disabled={busy}
            className="flex items-center gap-2 border border-coral text-coral-dark py-2.5 px-4 rounded-sm text-sm font-semibold hover:bg-coral-strong hover:text-white transition-colors disabled:opacity-60">
            <Send className="h-4 w-4" /> Publier maintenant
          </button>
        </div>
      </div>
    </div>
  );
}
